import {
  isOfficialLoginMethod,
  LOGIN_METHOD,
  type LoginRequest,
  type Session,
} from '@gofranz/common';
import {
  LoginCallbackPage,
  LoginPage,
  MagicLinkLoginPage,
  NewsPage,
  PrivacyPage,
  SignupPage,
  SupportPage,
  TermsPage,
  useLanguageAwareRouting,
} from '@gofranz/common-components';
import { Image, useComputedColorScheme } from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../constants';
import { AccountBillingUsagePage } from '../../pages/Account/Billing/Usage.page';
import { DataPrivacyPage } from '../../pages/Account/DataPrivacy.page';
import { AccountDocsPage } from '../../pages/Account/Docs.page';
import { AccountProfilePage } from '../../pages/Account/Profile.page';
import { AccountSecurityLogPage } from '../../pages/Account/SecurityLog.page';
import { ChatPage } from '../../pages/Chat.page';
import { DocsPage } from '../../pages/Docs.page';
import { HomePage } from '../../pages/Home.page';
import { PricingPage } from '../../pages/Pricing.page';
import { useRustyState } from '../../state';
import classes from '../Common/Title.module.css';
import { Footer } from './Footer';

interface LanguageRoutesProps {
  languagePrefix?: string;
}

export function LanguageRoutes({ languagePrefix }: LanguageRoutesProps) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  const brandLogo = <Image src="/logo.svg" alt={t('brand.logoAlt')} w={48} h={48} fit="contain" />;
  const brandName = (
    <>
      {t('brand.name')}
      {t('brand.nameSecond')}
    </>
  );

  React.useEffect(() => {
    if (languagePrefix && languagePrefix !== i18n.language) {
      i18n.changeLanguage(languagePrefix);
    } else if (!languagePrefix && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [languagePrefix, i18n]);

  // Track 9: refresh identity claims from /v1/a/whoami on boot and on
  // tab-focus / visibility transitions so the account widget shows the
  // freshest email / displayName without periodic polling. Debounced to a
  // 5-second minimum interval because focus events fire aggressively.
  React.useEffect(() => {
    const auth = useRustyState.getState().api?.auth;
    if (!auth) return;

    let lastCallAt = 0;
    const minIntervalMs = 5_000;

    const callWhoami = () => {
      const now = Date.now();
      if (now - lastCallAt < minIntervalMs) return;
      if (!auth.hasValidAccessToken()) return;
      lastCallAt = now;
      auth.whoami().catch((err: unknown) => {
        console.warn('whoami refresh failed; non-fatal', err);
      });
    };

    // Boot: kick off once we know we have a valid access token. Don't block
    // initial render — the call is fire-and-forget.
    callWhoami();

    const onFocus = () => callWhoami();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') callWhoami();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const setSession = (session: Session) => {
    useRustyState.getState().api.auth?.setSession(session);
  };

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/chat" element={<ChatPage />} />
      <Route
        path="/news"
        element={<NewsPage blogBaseUrl="https://blog.gpt-muse.com" footer={<Footer />} />}
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route
        path="/login"
        element={
          <LoginPage
            login={async (loginRequest: LoginRequest) => {
              if (isOfficialLoginMethod(loginRequest.type)) {
                return await useRustyState.getState().api.auth!.login(loginRequest);
              }
              throw new Error('Unsupported login method');
            }}
            loginChallenge={useRustyState.getState().loginChallenge}
            verify2FA={useRustyState.getState().verify2FA}
            onLoginSuccess={() => {
              nav(createLanguageURL('/chat'));
            }}
            logo={brandLogo}
            brandName={brandName}
            loginMethods={[
              LOGIN_METHOD.NOSTR,
              LOGIN_METHOD.GITHUB,
              LOGIN_METHOD.EMAIL_MAGIC_LINK,
              LOGIN_METHOD.OIDC,
            ]}
          />
        }
      />
      <Route
        path="/login/callback"
        element={
          <LoginCallbackPage
            setSession={setSession}
            titleClassName={classes.title}
            apiBaseUrl={API_BASE_URL}
            onLoginSuccess={() => {
              nav(createLanguageURL('/chat'));
            }}
          />
        }
      />
      <Route
        path="/login/magic-link"
        element={
          <MagicLinkLoginPage
            loginChallenge={useRustyState.getState().loginChallenge}
            titleGradient={{ from: 'blue', to: 'teal' }}
            titleClassName={classes.title}
            onLoginSuccess={() => {
              nav(createLanguageURL('/chat'));
            }}
          />
        }
      />
      <Route
        path="/privacy"
        element={<PrivacyPage supportEmail="support@gpt-muse.com" footer={<Footer />} />}
      />
      <Route
        path="/terms"
        element={<TermsPage supportEmail="support@gpt-muse.com" footer={<Footer />} />}
      />
      <Route
        path="/signup"
        element={
          <SignupPage
            backgroundImage={isDark ? '/login-background_dark.jpg' : '/login-background.jpg'}
            loginMethods={[LOGIN_METHOD.NOSTR, LOGIN_METHOD.GITHUB, LOGIN_METHOD.EMAIL_MAGIC_LINK]}
          />
        }
      />
      <Route path="/account/billing/usage" element={<AccountBillingUsagePage />} />
      <Route path="/account/docs" element={<AccountDocsPage />} />
      <Route path="/account/data-privacy" element={<DataPrivacyPage />} />
      <Route
        path="/account/profile"
        element={
          <AccountProfilePage serviceDomain="gpt-muse.com" serviceEmail="hello@gpt-muse.com" />
        }
      />
      <Route path="/account/security-log" element={<AccountSecurityLogPage />} />
      <Route
        path="/account/support"
        element={
          <SupportPage
            session={useRustyState.getState().api?.auth?.getSession()}
            serviceName="GPTMuse"
          />
        }
      />
    </Routes>
  );
}
